import { CONTACT_EMAIL, ExtLink, LegalLayout, Section } from './LegalLayout';

const LIST = 'list-disc space-y-2 pl-5 marker:text-white/30';
const STRONG = 'font-medium text-white/85';

export default function PrivacyPolicy() {
	return (
		<LegalLayout title="Privacy Policy" effectiveDate="September 21, 2026">
			<p>
				Enflux is a social media management platform that lets agencies and businesses plan, publish, and
				measure content across their social accounts. This policy explains what information Enflux collects,
				how we use it, and the choices you have. It applies to enflux.cc and the Enflux application.
			</p>

			<Section title="Information we collect">
				<ul className={LIST}>
					<li>
						<span className={STRONG}>Account information:</span> your name, email address, and a password
						(stored only as a salted hash — we never store your password in plain text).
					</li>
					<li>
						<span className={STRONG}>Workspace and brand information:</span> brand names, logos, colors, voice
						and tone guidelines, target audience, website, and services you enter to configure a workspace.
					</li>
					<li>
						<span className={STRONG}>Content you create:</span> post captions, uploaded images and videos,
						schedules, queues, and approvals.
					</li>
					<li>
						<span className={STRONG}>Connected social accounts:</span> when you connect a platform, we receive
						an access token and basic profile details (account name, ID, and profile picture) from that
						platform, plus the data described under “Data from connected platforms” below.
					</li>
					<li>
						<span className={STRONG}>Technical data:</span> a session token kept in your browser's local
						storage so you stay signed in, and your theme preference. We do not use advertising or
						third-party tracking cookies.
					</li>
				</ul>
			</Section>

			<Section title="Data from connected platforms">
				<p>
					You choose which accounts to connect — Facebook, Instagram, LinkedIn, X, TikTok, YouTube, and Google
					Business Profile. For each one, Enflux only requests the permissions needed to do what you ask of it:
				</p>
				<ul className={LIST}>
					<li>Publish the posts, videos, events, and offers you create or schedule in Enflux.</li>
					<li>
						Read account and post metrics (such as followers, impressions, engagements, likes, and comments)
						to power your analytics dashboard and monthly reports.
					</li>
					<li>Read comments and messages where the platform allows it, to show them in your inbox.</li>
				</ul>
			</Section>

			<Section title="Google user data">
				<p>When you connect Google services, Enflux requests the following access:</p>
				<ul className={LIST}>
					<li>
						<span className={STRONG}>Google Business Profile</span> (
						<code className="text-[13px] text-white/75">business.manage</code>): to list your business
						location, publish posts, events, and offers you create in Enflux, and read performance metrics
						(Maps and Search impressions, calls, website clicks, and direction requests) for your reports.
					</li>
					<li>
						<span className={STRONG}>YouTube</span> (
						<code className="text-[13px] text-white/75">youtube.upload</code>,{' '}
						<code className="text-[13px] text-white/75">youtube.readonly</code>): to upload the videos you
						schedule in Enflux to your channel, and read channel and video statistics for your analytics.
					</li>
				</ul>
				<p>
					Enflux's use and transfer of information received from Google APIs to any other app will adhere to
					the{' '}
					<ExtLink href="https://developers.google.com/terms/api-services-user-data-policy">
						Google API Services User Data Policy
					</ExtLink>
					, including the Limited Use requirements. Specifically:
				</p>
				<ul className={LIST}>
					<li>We use Google user data only to provide and improve the features you use in Enflux.</li>
					<li>We do not sell Google user data or use it for advertising.</li>
					<li>
						We do not use Google user data to develop, improve, or train generalized artificial intelligence
						or machine learning models.
					</li>
					<li>
						No person at Enflux reads your Google user data unless you give us permission (for example, to
						resolve a support request), it is needed for security purposes, or it is required by law.
					</li>
				</ul>
				<p>
					Enflux uses YouTube API Services. By connecting YouTube you also agree to be bound by the{' '}
					<ExtLink href="https://policies.google.com/privacy">Google Privacy Policy</ExtLink>.
				</p>
			</Section>

			<Section title="How we use information">
				<ul className={LIST}>
					<li>To operate Enflux: publish and schedule content, sync analytics, and build reports.</li>
					<li>
						To generate AI suggestions — captions, hashtags, content ideas, and report summaries — using your
						brand details and performance data. These are sent to our AI providers only to produce the result
						you asked for.
					</li>
					<li>To send service emails, such as invitations, approvals, and notifications.</li>
					<li>To keep the service secure, prevent abuse, and fix problems.</li>
				</ul>
			</Section>

			<Section title="How we share information">
				<p>We do not sell your personal information. We share it only:</p>
				<ul className={LIST}>
					<li>
						<span className={STRONG}>With the social platforms you connect</span>, when publishing content or
						requesting metrics on your behalf.
					</li>
					<li>
						<span className={STRONG}>With service providers</span> that run Enflux for us: Cloudflare
						(hosting, database, file storage, and AI inference), xAI (AI text and image generation), and a
						social data provider used for public trend and creator research.
					</li>
					<li>
						<span className={STRONG}>With your team:</span> members of your agency or workspace can see the
						workspace's content, connected accounts, and analytics according to their role. Report links you
						share can be opened by anyone who has the link until they expire or are revoked.
					</li>
					<li>When required by law, or to protect the rights and safety of Enflux and its users.</li>
				</ul>
			</Section>

			<Section title="Storage and security">
				<p>
					Enflux runs on Cloudflare infrastructure. Access tokens for connected social accounts are encrypted
					with AES-GCM before they are stored, all traffic is encrypted in transit over HTTPS, and passwords are
					hashed with PBKDF2. Access to data inside the app is restricted by agency and workspace membership.
				</p>
			</Section>

			<Section title="Retention and deletion">
				<ul className={LIST}>
					<li>
						<span className={STRONG}>Disconnecting an account</span> in Settings → Social Accounts immediately
						deletes its stored access tokens from Enflux.
					</li>
					<li>
						You can also revoke Enflux's access to your Google account at any time from your{' '}
						<ExtLink href="https://myaccount.google.com/permissions">Google Account permissions page</ExtLink>
						.
					</li>
					<li>
						We keep the rest of your data for as long as your account is active. To delete your account and
						its data, email{' '}
						<a href={`mailto:${CONTACT_EMAIL}`} className="text-[#00c7f9] hover:underline">
							{CONTACT_EMAIL}
						</a>{' '}
						and we will delete it within 30 days, except where we must keep information to meet legal
						obligations.
					</li>
				</ul>
			</Section>

			<Section title="Your rights">
				<p>
					You can access, correct, export, or delete your personal information. Most of it can be edited
					directly in Settings; for anything else, contact us at the address below.
				</p>
			</Section>

			<Section title="Children">
				<p>Enflux is a business tool and is not directed to children under 16.</p>
			</Section>

			<Section title="Changes to this policy">
				<p>
					If we make material changes, we will update the effective date above and notify account owners by
					email or in the app.
				</p>
			</Section>

			<Section title="Contact">
				<p>
					Questions about this policy or your data:{' '}
					<a href={`mailto:${CONTACT_EMAIL}`} className="text-[#00c7f9] hover:underline">
						{CONTACT_EMAIL}
					</a>
				</p>
			</Section>
		</LegalLayout>
	);
}
